import { countTasks, type TaskCounts } from "@wiki/core";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { useMemo } from "react";

import { headingEntries, type HeadingEntry } from "./plugins/headings";

export interface TocEntry extends HeadingEntry {
  /** Nesting depth relative to the shallowest heading in the document. */
  depth: number;
}

/**
 * The outline panel's data (`editor.md` §13): headings to level 4, in document
 * order, each with the anchor id `@wiki/core` computes, and headings inside
 * tables excluded.
 */
export function useTableOfContents(doc: ProseMirrorNode, maxLevel = 4): TocEntry[] {
  return useMemo(() => {
    const entries = headingEntries(doc).filter((entry) => entry.level <= maxLevel);
    const shallowest = entries.reduce((min, entry) => Math.min(min, entry.level), maxLevel);
    return entries.map((entry) => ({ ...entry, depth: entry.level - shallowest }));
  }, [doc, maxLevel]);
}

/**
 * The document's task summary (§14). Recomputed on every change; this is what
 * document lists render as a progress indicator.
 */
export function useTaskCounts(doc: ProseMirrorNode): TaskCounts {
  return useMemo(() => countTasks(doc), [doc]);
}
